import React, { useState } from 'react';
import { Wand2, Bot, Zap, Settings, X, Check, AlertCircle, Sparkles, Brain, Workflow } from 'lucide-react';
import { AIWorkflowBuilder } from './AIWorkflowBuilder';
import { RealAgentBuilder } from './RealAgentBuilder';

interface AgentBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenAssistant: () => void;
  onSetAssistantInitialMessage: (message: string) => void;
}

export function AgentBuilder({ isOpen, onClose, onOpenAssistant, onSetAssistantInitialMessage }: AgentBuilderProps) {
  const [activeTab, setActiveTab] = useState<'custom' | 'template' | 'workflow' | 'ai-workflow'>('custom');
  const [showAIWorkflowBuilder, setShowAIWorkflowBuilder] = useState(false);
  const [showRealAgentBuilder, setShowRealAgentBuilder] = useState(false);

  const handleAskAssistant = (question: string) => {
    onSetAssistantInitialMessage(question);
    onOpenAssistant();
  };

  const handleOpenAIWorkflowBuilder = () => {
    setShowAIWorkflowBuilder(true);
  };

  const handleCloseAIWorkflowBuilder = () => {
    setShowAIWorkflowBuilder(false);
  };

  const handleOpenRealAgentBuilder = () => {
    setShowRealAgentBuilder(true);
  };

  const handleCloseRealAgentBuilder = () => {
    setShowRealAgentBuilder(false);
  };

  if (!isOpen) return null;

  if (showAIWorkflowBuilder) {
    return (
      <AIWorkflowBuilder 
        isOpen={true} 
        onClose={handleCloseAIWorkflowBuilder} 
      />
    );
  }

  if (showRealAgentBuilder) {
    return (
      <RealAgentBuilder 
        isOpen={true} 
        onClose={handleCloseRealAgentBuilder} 
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="flex items-center gap-3">
            <Wand2 className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-semibold">Agent Builder</h2>
              <p className="text-sm opacity-90">Create custom AI agents</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('custom')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'custom'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Bot className="w-4 h-4" />
              Custom Agent
            </button>
            <button
              onClick={() => setActiveTab('template')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'template'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Zap className="w-4 h-4" />
              From Template
            </button>
            <button
              onClick={() => setActiveTab('workflow')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'workflow'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Workflow className="w-4 h-4" />
              A2A Workflow
            </button>
            <button
              onClick={() => setActiveTab('ai-workflow')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'ai-workflow'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Brain className="w-4 h-4" />
              AI Workflow
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'custom' && (
            <div className="space-y-6">
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                  <Bot className="w-5 h-5 text-purple-600" />
                  Create Custom Agent
                </h3>
                <p className="text-purple-700 mb-4">
                  Build a custom AI agent from scratch with your own configuration and capabilities.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-purple-900 mb-1">
                      Agent Name
                    </label>
                    <input
                      type="text"
                      placeholder="My Custom Agent"
                      className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-purple-900 mb-1">
                      Description
                    </label>
                    <textarea
                      placeholder="Describe what your agent does..."
                      rows={3}
                      className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-purple-900 mb-1">
                      Platform
                    </label>
                    <select
                      className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="openai">OpenAI</option>
                      <option value="anthropic">Anthropic Claude</option>
                      <option value="gemini">Google Gemini</option>
                      <option value="mistral">Mistral AI</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>
                <button
                  onClick={() => handleAskAssistant("I need help creating a custom AI agent. Can you guide me through the process?")}
                  className="mt-4 text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                >
                  Need help? Ask AI Assistant
                </button>
                
                <button
                  onClick={handleOpenRealAgentBuilder}
                  className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors"
                >
                  <Bot className="w-4 h-4" />
                  Build Real Agent with Tools
                </button>
              </div>
            </div>
          )}

          {activeTab === 'template' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-600" />
                  Start from Template
                </h3>
                <p className="text-blue-700 mb-4">
                  Choose from pre-built agent templates to get started quickly.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-blue-200 rounded-lg p-4 hover:bg-blue-50 cursor-pointer">
                    <h4 className="font-medium text-blue-900 mb-1">Customer Support Bot</h4>
                    <p className="text-sm text-blue-700 mb-2">AI assistant for customer inquiries and support</p>
                    <div className="text-xs text-blue-600">Complexity: Beginner</div>
                  </div>
                  <div className="border border-blue-200 rounded-lg p-4 hover:bg-blue-50 cursor-pointer">
                    <h4 className="font-medium text-blue-900 mb-1">Content Generator</h4>
                    <p className="text-sm text-blue-700 mb-2">Creates blog posts, social media content, and more</p>
                    <div className="text-xs text-blue-600">Complexity: Intermediate</div>
                  </div>
                  <div className="border border-blue-200 rounded-lg p-4 hover:bg-blue-50 cursor-pointer">
                    <h4 className="font-medium text-blue-900 mb-1">Data Analysis Assistant</h4>
                    <p className="text-sm text-blue-700 mb-2">Analyzes data and generates insights</p>
                    <div className="text-xs text-blue-600">Complexity: Advanced</div>
                  </div>
                  <div className="border border-blue-200 rounded-lg p-4 hover:bg-blue-50 cursor-pointer">
                    <h4 className="font-medium text-blue-900 mb-1">Research Assistant</h4>
                    <p className="text-sm text-blue-700 mb-2">Helps with research, summarization, and fact-checking</p>
                    <div className="text-xs text-blue-600">Complexity: Intermediate</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'workflow' && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <Workflow className="w-5 h-5 text-green-600" />
                  Create A2A Workflow
                </h3>
                <p className="text-green-700 mb-4">
                  Design a multi-agent workflow where specialized agents work together to solve complex problems.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-green-900 mb-1">
                      Workflow Name
                    </label>
                    <input
                      type="text"
                      placeholder="My Multi-Agent Workflow"
                      className="w-full px-4 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-900 mb-1">
                      Description
                    </label>
                    <textarea
                      placeholder="Describe what your workflow does..."
                      rows={3}
                      className="w-full px-4 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <button
                  className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Workflow className="w-4 h-4" />
                  Open Workflow Designer
                </button>
              </div>
            </div>
          )}

          {activeTab === 'ai-workflow' && (
            <div className="space-y-6">
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
                <h3 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                  <Brain className="w-5 h-5 text-indigo-600" />
                  AI-Driven Workflow Generation
                </h3>
                <p className="text-indigo-700 mb-4">
                  Describe your task in natural language, and our AI will automatically generate a multi-agent workflow for you.
                </p>
                <div className="p-4 bg-white rounded-lg border border-indigo-200 mb-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-indigo-500 mt-1" />
                    <div>
                      <h4 className="font-medium text-indigo-900 mb-1">How It Works</h4>
                      <ol className="text-sm text-indigo-700 space-y-1 list-decimal list-inside">
                        <li>Describe your task in natural language</li>
                        <li>AI analyzes your requirements</li>
                        <li>Identifies necessary steps and agents</li>
                        <li>Generates an optimized workflow</li>
                        <li>You can review and customize the result</li>
                      </ol>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleOpenAIWorkflowBuilder}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  Generate AI Workflow
                </button>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Example Use Cases</h3>
                <div className="space-y-3">
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-1">Research Assistant Network</h4>
                    <p className="text-sm text-gray-600">A multi-agent system that coordinates research tasks across specialized agents for search, summarization, fact-checking, and citation.</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-1">Data Analysis Pipeline</h4>
                    <p className="text-sm text-gray-600">A data processing system where agents handle different stages: collection, cleaning, analysis, and visualization.</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-1">Content Creation Suite</h4>
                    <p className="text-sm text-gray-600">A content generation system with specialized agents for ideation, writing, editing, and SEO optimization.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}